import { Fragment, ReactNode, useEffect, useState } from 'react';
import { HiPlus, HiTrash } from 'react-icons/hi';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from 'src/components/_common/Button/Button';
import DateOnlyInput from 'src/components/_common/DateOnlyInput/DateOnlyInput';
import Attendance, { AttendanceInput } from 'src/models/Attendance';
import DateOnly from 'src/models/common/DateOnly';

interface AttendanceRowState {
  key: string;
  id?: number;
  date: DateOnly | null;
  scheduledHours: number | null;
  attendedHours: number | null;
  approvedBy: number | null;
}

interface AttendanceListProps {
  entries: Attendance[];
  mode: 'student' | 'supervisor';
  readOnly?: boolean;
  isSaving?: boolean;
  onSave?: (entries: AttendanceInput[]) => void;
  onApprove?: (attendanceId: number) => void;
  onUnapprove?: (attendanceId: number) => void;
  renderRowDetails?: (attendanceId: number) => ReactNode;
}

let nextKey = 0;
const makeKey = () => `new-${++nextKey}`;

function toRowState(entry: Attendance): AttendanceRowState {
  return {
    key: `id-${entry.id}`,
    id: entry.id,
    date: entry.date,
    scheduledHours: entry.scheduledHours,
    attendedHours: entry.attendedHours,
    approvedBy: entry.approvedBy,
  };
}

function toInput(row: AttendanceRowState): AttendanceInput {
  return {
    id: row.id,
    date: row.date as DateOnly,
    scheduledHours: row.scheduledHours ?? 0,
    attendedHours: row.attendedHours ?? null,
  };
}

export default function AttendanceList({
  entries,
  mode,
  readOnly = false,
  isSaving = false,
  onSave,
  onApprove,
  onUnapprove,
  renderRowDetails,
}: AttendanceListProps) {
  const { formatMessage } = useIntl();
  const [rows, setRows] = useState<AttendanceRowState[]>(() => entries.map(toRowState));
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  useEffect(() => {
    setRows(entries.map(toRowState));
  }, [entries]);

  function updateRow(key: string, patch: Partial<AttendanceRowState>) {
    setRows(prev => prev.map(row => (row.key === key ? { ...row, ...patch } : row)));
  }

  function addRow() {
    setRows(prev => [
      ...prev,
      {
        key: makeKey(),
        date: null,
        scheduledHours: null,
        attendedHours: null,
        approvedBy: null,
      },
    ]);
  }

  function removeRow(key: string) {
    setRows(prev => prev.filter(row => row.key !== key));
  }

  function toggleExpanded(id: number) {
    setExpandedIds(prev => (prev.includes(id) ? prev.filter(expandedId => expandedId !== id) : [...prev, id]));
  }

  function handleSave() {
    onSave?.(rows.map(toInput));
  }

  const showApproveColumn = mode === 'supervisor';
  const allowEdit = mode === 'student' && !readOnly;
  const columnCount = 3 + (showApproveColumn ? 1 : 0) + (allowEdit ? 1 : 0) + (renderRowDetails ? 1 : 0);

  return (
    <div className="mt-4">
      <table className="w-full">
        <thead>
          <tr className="text-left">
            <th className="py-2"><FormattedMessage id="attendance-column-date" /></th>
            <th className="py-2"><FormattedMessage id="attendance-column-scheduled-hours" /></th>
            <th className="py-2"><FormattedMessage id="attendance-column-attended-hours" /></th>
            {showApproveColumn && (
              <th className="py-2"><FormattedMessage id="attendance-column-approved" /></th>
            )}
            {allowEdit && <th className="py-2" aria-hidden />}
            {renderRowDetails && <th className="py-2" aria-hidden />}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={columnCount} className="py-4 text-center text-customGray">
                <FormattedMessage id="attendance-empty" />
              </td>
            </tr>
          )}
          {rows.map(row => {
            const isApproved = row.approvedBy != null;
            const lockedRow = isApproved || readOnly;
            const lockScheduleFields = mode === 'supervisor' || lockedRow || (row.attendedHours != null && row.id != null);
            const lockAttendedField = mode === 'supervisor' || lockedRow;

            const isExpanded = row.id != null && expandedIds.includes(row.id);

            return (
              <Fragment key={row.key}>
                <tr className="border-t">
                  <td className="py-2 pr-2">
                    <DateOnlyInput
                      value={row.date}
                      setValue={value => updateRow(row.key, { date: value })}
                      disabled={lockScheduleFields}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <input
                      type="number"
                      min={0}
                      className="w-full border rounded px-2 py-1"
                      value={row.scheduledHours ?? ''}
                      disabled={lockScheduleFields}
                      onChange={e => updateRow(row.key, {
                        scheduledHours: e.target.value === '' ? null : Number(e.target.value),
                      })}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <input
                      type="number"
                      min={0}
                      className="w-full border rounded px-2 py-1"
                      value={row.attendedHours ?? ''}
                      disabled={lockAttendedField}
                      onChange={e => updateRow(row.key, {
                        attendedHours: e.target.value === '' ? null : Number(e.target.value),
                      })}
                    />
                  </td>
                  {showApproveColumn && (
                    <td className="py-2 pr-2 text-center">
                      <input
                        type="checkbox"
                        checked={isApproved}
                        disabled={readOnly || row.id == null || row.attendedHours == null}
                        aria-label={formatMessage({ id: 'attendance-approve-aria' })}
                        onChange={e => {
                          if (row.id == null) return;
                          if (e.target.checked) onApprove?.(row.id);
                          else onUnapprove?.(row.id);
                        }}
                      />
                    </td>
                  )}
                  {allowEdit && (
                    <td className="py-2 pr-2 w-10 text-right">
                      {!isApproved && (
                        <button
                          type="button"
                          aria-label={formatMessage({ id: 'attendance-remove-row-aria' })}
                          onClick={() => removeRow(row.key)}
                          className="text-danger"
                        >
                          <HiTrash />
                        </button>
                      )}
                    </td>
                  )}
                  {renderRowDetails && (
                    <td className="py-2 pr-2 text-right">
                      {row.id != null && (
                        <Button size="small" layout="link" aria-expanded={isExpanded} onClick={() => toggleExpanded(row.id!)}>
                          <FormattedMessage id="attendance-log-toggle" />
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
                {isExpanded && renderRowDetails && (
                  <tr>
                    <td colSpan={columnCount} className="pb-4">
                      {renderRowDetails(row.id!)}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {allowEdit && (
        <div className="flex justify-between mt-4">
          <Button size="small" onClick={addRow} iconLeft={HiPlus}>
            <FormattedMessage id="attendance-add-row" />
          </Button>
          <Button onClick={handleSave} isSaving={isSaving}>
            <FormattedMessage id="save" />
          </Button>
        </div>
      )}
    </div>
  );
}
